import React, { useState, useEffect } from 'react';
import { differenceInMinutes, format } from 'date-fns';

import BookOneOffSession from 'coaching/components/CoachingHomeView/BookOneOffSession';
import BookRecurringSession from 'coaching/components/CoachingHomeView/BookRecurringSession';
import BookSessionConfirmationSidebar from 'coaching/components/CoachingHomeView/BookSessionConfirmationSidebar';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import CoachAvatarWithSubject from 'coaching/components/CoachAvatarWithSubject';
import CoachingSessionFrequencySelection from 'coaching/components/CoachingHomeView/CoachingSessionFrequencySelection';
import Divider from 'components/Divider';
import ErrorReporter from 'app/components/ErrorReporter';
import EventTracking from 'lib/EventTracking';
import Icon from 'components/Icon';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import SidebarFooter from 'components/SidebarFooter';
import Text from 'components/Text';
import TimezoneHelper from 'lib/timezones';
import ToastActions from 'actions/ToastActions';
import GetPossibleCoachingSessionsForCoachContainer from 'coaching/containers/GetPossibleCoachingSessionsForCoachContainer';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { createCoachingSession } from 'coaching/actions/CoachingActions';
import { getNextNCoachingDays } from 'coaching/lib/timeIntervals';
import {
  CoachMetadata,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { TimeslotFrequencyInput, CoachingEventDefinitionInput } from 'globalTypes';
import GET_COACHING_HOME_DASHBOARD_QUERY from 'coaching/queries/GetCoachingHomeDashboard.gql';

const userTimezone = TimezoneHelper.userTimezoneDefaultValue ?? 'America/Los_Angeles';
const defaultRange = getNextNCoachingDays(6, 0, userTimezone);
const defaultStart = defaultRange[0]?.toISOString();
const defaultEnd = defaultRange
  .slice(-1)
  .pop()
  ?.toISOString();

type BookingSteps = 'frequency-selection' | 'book-session' | 'confirmation';

type Props = {
  subjectName: string;
  sessionTools: CoachingSessionTools;
  participantUuid: string;
  coach: CoachMetadata;
  onClose: () => void;
  defaultFrequency?: TimeslotFrequencyInput;
  defaultStep?: BookingSteps;
  showFrequencySelect?: boolean;
};

export default function BookNewCoachingSessionSidebar({
  sessionTools,
  subjectName,
  coach,
  onClose,
  participantUuid,
  defaultFrequency = TimeslotFrequencyInput.weekly,
  defaultStep = 'frequency-selection',
  showFrequencySelect = true,
}: Props) {
  const [step, setStep] = useState<BookingSteps>(defaultStep);
  const [weekOffset, setWeekOffset] = useState<number>(0);
  const [startDateTimeRange, setStartDateTimeRange] = useState<string>(defaultStart);
  const [endDateTimeRange, setEndDateTimeRange] = useState<string | undefined>(defaultEnd);
  const [frequency, setFrequency] = useState<TimeslotFrequencyInput>(defaultFrequency);
  const [loadingEventDefinition, setLoadingEventDefinition] = useState(false);
  const [eventDefinition, setEventDefinition] = useState<CoachingEventDefinitionInput>();
  const [submitting, setSubmitting] = useState(false);

  const eventPrefix = `${coachingHomeEventPrefix}.add_session_sidebar`;
  const eventPrefixWithFrequency =
    frequency === TimeslotFrequencyInput.once
      ? `${eventPrefix}.book_one_off_session`
      : `${eventPrefix}.book_recurring_session`;
  function trackKey(key: string) {
    EventTracking.trackKey(`${eventPrefix}.${key}`);
  }

  useEffect(() => {
    const newRange = getNextNCoachingDays(6, weekOffset, userTimezone);
    setStartDateTimeRange(newRange[0]?.toISOString());
    setEndDateTimeRange(
      newRange
        .slice(-1)
        .pop()
        ?.toISOString()
    );
  }, [weekOffset]);

  useEffect(() => {
    // reset state if we change the participant/subject/coach
    setStep(defaultStep);
    setEventDefinition(undefined);
    setWeekOffset(0);
  }, [participantUuid, sessionTools.subject.uuid, coach.uuid]);

  if (!startDateTimeRange || !endDateTimeRange) {
    return (
      <ErrorReporter error="BookNewCoachingSessionSidebar - tried to render without a dateTimeRange">
        Oops! Looks like something went wrong. Please try refreshing the page.
      </ErrorReporter>
    );
  }

  async function handleSubmit() {
    if (eventDefinition) {
      setSubmitting(true);
      await createCoachingSession(
        {
          participantUuid,
          coachUuid: coach.uuid,
          eventDefinition,
        },
        [
          {
            query: GET_COACHING_HOME_DASHBOARD_QUERY,
            variables: {},
          },
        ],
        () => setStep('confirmation')
      );
      setSubmitting(false);
    } else {
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to book a session without selecting a date and time. Please try again.'
      );
    }
  }

  const coachAvatarComponent = (
    <BoxBody size="xsmall">
      <CoachAvatarWithSubject subjectName={subjectName} coachFullName={coach.name} />
    </BoxBody>
  );

  const stepToComponentMap: Record<BookingSteps, React.ReactElement> = {
    'frequency-selection': (
      <>
        {coachAvatarComponent}
        <Divider size="flush" />
        <BoxBody size="xsmall">
          <CoachingSessionFrequencySelection
            data-test="frequency-selection-step"
            eventPrefix={`${eventPrefix}.frequency_selection`}
            interfaceType="buttons"
            onSelectWeekly={() => {
              setStep('book-session');
              setFrequency(TimeslotFrequencyInput.weekly);
              trackKey(`${eventPrefix}.frequency_selection.weekly.click`);
            }}
            onSelectEveryOtherWeek={() => {
              setStep('book-session');
              setFrequency(TimeslotFrequencyInput.everyOtherWeek);
              trackKey(`${eventPrefix}.frequency_selection.every_other_week.click`);
            }}
            onSelectOnce={() => {
              setStep('book-session');
              setFrequency(TimeslotFrequencyInput.once);
              trackKey(`${eventPrefix}.frequency_selection.once.click`);
            }}
          />
        </BoxBody>
      </>
    ),
    'book-session': (
      <>
        {coachAvatarComponent}
        <Divider size="flush" />
        <GetPossibleCoachingSessionsForCoachContainer
          subjectUuid={sessionTools.subject.uuid}
          coachUuid={coach.uuid}
          startDateTimeRange={startDateTimeRange}
          endDateTimeRange={endDateTimeRange}
          frequency={frequency}>
          {props => (
            <>
              {showFrequencySelect && (
                <>
                  <BoxBody size="small">
                    <CoachingSessionFrequencySelection
                      interfaceType="select"
                      onSelectWeekly={() => {
                        setFrequency(TimeslotFrequencyInput.weekly);
                      }}
                      onSelectEveryOtherWeek={() => {
                        setFrequency(TimeslotFrequencyInput.everyOtherWeek);
                      }}
                      onSelectOnce={() => {
                        setFrequency(TimeslotFrequencyInput.once);
                      }}
                      selectedFrequency={frequency}
                    />
                  </BoxBody>
                  <Divider size="flush" />
                </>
              )}
              {frequency === TimeslotFrequencyInput.once ? (
                <BookOneOffSession
                  {...props}
                  data-test="book-one-off-session"
                  startDateTimeRange={startDateTimeRange}
                  endDateTimeRange={endDateTimeRange}
                  weekOffset={weekOffset}
                  timezone={coach.timezone ?? userTimezone}
                  onSelectSlot={setEventDefinition}
                  onWeekForward={() => setWeekOffset(prev => prev + 1)}
                  onWeekBack={() => setWeekOffset(prev => prev - 1)}
                  eventPrefix={eventPrefixWithFrequency}
                />
              ) : (
                <BookRecurringSession
                  {...props}
                  data-test="book-recurring-session"
                  participantUuid={participantUuid}
                  coachUuid={coach.uuid}
                  timezone={coach.timezone ?? userTimezone}
                  frequency={frequency}
                  onSelectSlot={setEventDefinition}
                  onLoadEventDefinition={setLoadingEventDefinition}
                />
              )}
              <BookSessionsFooter
                disabled={!eventDefinition || loadingEventDefinition}
                submitting={submitting}
                onSubmit={handleSubmit}
                eventDefinition={eventDefinition}
                frequency={frequency}
                eventPrefix={eventPrefixWithFrequency}
              />
            </>
          )}
        </GetPossibleCoachingSessionsForCoachContainer>
      </>
    ),
    confirmation: (
      <BookSessionConfirmationSidebar
        data-test="book-session-confirmation-step"
        streamUuid={sessionTools.streamUuid}
        eventDefinition={eventDefinition}
        eventPrefix={`${eventPrefixWithFrequency}_confirmation`}
      />
    ),
  };

  return (
    <>
      <BoxBody size="xsmall">
        <MediaObject figure="right">
          <Text color="graphite">Book sessions</Text>
          <Button
            level="icon"
            color="chalkboard"
            onClick={onClose}
            eventKey={`${eventPrefixWithFrequency}.close.click`}>
            <Icon name="x" />
          </Button>
        </MediaObject>
      </BoxBody>
      <Divider size="flush" />
      {stepToComponentMap[step]}
    </>
  );
}

function BookSessionsFooter({
  disabled,
  submitting,
  onSubmit,
  eventDefinition,
  frequency,
  eventPrefix,
}: {
  disabled: boolean;
  submitting: boolean;
  onSubmit: () => Promise<void> | void;
  eventDefinition?: CoachingEventDefinitionInput;
  frequency: TimeslotFrequencyInput;
  eventPrefix?: string;
}) {
  return (
    <SidebarFooter>
      <BoxBody size="small">
        <EventExplanation eventDefinition={eventDefinition} frequency={frequency} />
        <Button
          data-test="book-sessions-button"
          eventKey={eventPrefix && `${eventPrefix}.book_session.click`}
          full
          disabled={disabled}
          loading={submitting}
          onClick={onSubmit}>
          Book sessions
        </Button>
      </BoxBody>
    </SidebarFooter>
  );
}

function EventExplanation({
  eventDefinition,
  frequency,
}: {
  eventDefinition?: CoachingEventDefinitionInput;
  frequency: TimeslotFrequencyInput;
}) {
  if (!eventDefinition) return null;
  const duration = differenceInMinutes(eventDefinition.endDateTime, eventDefinition.startDateTime);
  const firstInstanceText = (
    <Text weight="semibold">{format(eventDefinition?.startDateTime, 'ddd, MMM Do')}</Text>
  );
  const recurringText = (
    <>
      You will be billed ${duration} the day of each session beginning {firstInstanceText}
    </>
  );
  const oneOffText = (
    <>
      Your session will be scheduled for
      <br /> {firstInstanceText}. You will be billed ${duration} the day of the session.
    </>
  );
  const frequencyToTextMap: Record<TimeslotFrequencyInput, React.ReactNode> = {
    [TimeslotFrequencyInput.once]: oneOffText,
    [TimeslotFrequencyInput.everyOtherWeek]: recurringText,
    [TimeslotFrequencyInput.weekly]: recurringText,
  };

  return <Row size="small">{frequencyToTextMap[frequency]}</Row>;
}
